import React, {useContext, useEffect, useState} from 'react';
import {Alert, Button, Form, FormProps, Select, Slider, SliderSingleProps, Space} from 'antd';
import {useTranslation} from 'react-i18next';
import {AlarmLimit} from '../../../common/types';
import {AlarmLimitSelect} from '../../Wizard/FormItems';
import {PredictiveTrans} from '../Trans';
import {PredictionService} from '../../../common/services';
import {OutUnit} from '../../../common/constants/OutUnit';
import {AuthContext} from '../../../common/context';
import {IBox as Box} from '@elproag/predict';
import {BoxTable} from '../Overview/BoxTable';
import DateTimeUtils from '../../Infrastructure/DateTime/DateTimeUtils';
import {useForm} from 'antd/es/form/Form';

export type PredictiveFormState = {
	alarmLimit: AlarmLimit;
	alarmLimitOutUnit: number;
	box: Box;
	interventionBufferSeconds: number;
};

interface PredictiveFormProps {
	initialState?: PredictiveFormState;
	loading?: boolean;
	outUnit: OutUnit;
	onSubmitSuccess?: (predictiveFormState: PredictiveFormState) => void;
	onReset?: () => void;
	formProps?: FormProps;
}

export function PredictiveForm(props: Readonly<PredictiveFormProps>): React.JSX.Element {
	const [t] = useTranslation();
	const authContext = useContext(AuthContext);
	const orgId = authContext?.Organization?.Uuid;

	const [disabled, setDisabled] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [form] = useForm();
	const [boxes, setBoxes] = useState<Box[]>([]);
	const [boxId, setBoxId] = useState<string>(props.initialState?.box?.id);
	const box = boxes.find(b => b.id == boxId);
	const [interventionBufferHours, setInterventionBufferHours] = useState<number>(0);
	const [alarmLimit, setAlarmLimit] = useState<AlarmLimit>(props.initialState?.alarmLimit);

	useEffect(() => {
		if (!box) return;

		setInterventionBufferHours(maxInterventionBufferHours);
	}, [box]);

	const maxInterventionBufferHours = (box?.qualifiedDurationSeconds ?? 0) / 3600;

	let containerOptions = containerResponseToOptions(boxes);

	const marks: SliderSingleProps['marks'] = [...Array(maxInterventionBufferHours + 1).keys()]
		.filter((i, idx, array) => i % 4 == 0 || idx == array.length - 1)
		.reduce((record, currentValue) => {
			record[currentValue] = currentValue;
			return record;
		}, {} as Record<number, number>);

	function createErrorMessage(prev: string | null, message: string): string {
		let newMessage = prev ? `${prev}\n` : '';
		newMessage += message;
		return newMessage;
	}

	useEffect(() => {
		PredictionService.GetBoxes(orgId)
			.then(boxes => {
				if (boxes.length === 0)
					setErrorMessage(prev =>
						createErrorMessage(
							prev,
							'No boxes returned. Check if generic boxes are included in elproPREDICT settings, or contact SmartCAE.'
						)
					);
				else setBoxes(boxes);
			})
			.catch(error => {
				console.error(error);
				setErrorMessage(t('error.serviceError', {service: 'Predictive'}));
			});
	}, []);

	const onSubmitSuccess = (_values: any) => {
		setDisabled(true);
		const predictiveFormState: PredictiveFormState = {
			alarmLimit: alarmLimit,
			alarmLimitOutUnit: props.outUnit,
			box: boxes.find(c => c.id === boxId),
			interventionBufferSeconds: Math.min(maxInterventionBufferHours, interventionBufferHours) * 3600,
		};

		props.onSubmitSuccess?.(predictiveFormState);
	};

	function containerResponseToOptions(response: Box[]): {value: string; label: string}[] {
		try {
			return response.map(item => ({
				value: item.id,
				label: `${item.name} (${DateTimeUtils.formatSecondsAsDuration(item.qualifiedDurationSeconds)})`,
			}));
		} catch {
			return [];
		}
	}

	function reset(_event: React.MouseEvent<HTMLElement>) {
		setDisabled(false);
		setBoxId(undefined);
		setInterventionBufferHours(0);
		setAlarmLimit(undefined);
		form.resetFields();
		props.onReset?.();
	}

	return (
		<Form onFinish={onSubmitSuccess} {...props.formProps} disabled={disabled} form={form}>
			{errorMessage && <Alert message={t('error.title').toString()} description={errorMessage} type="error" showIcon />}
			<Form.Item
				label={t(PredictiveTrans.qualifiedProfile).toString()}
				name={t(PredictiveTrans.qualifiedProfile).toString()}
				rules={[{required: true}]}
				initialValue={props.initialState?.box?.id}
			>
				<Select options={containerOptions} onChange={setBoxId} />
			</Form.Item>

			{boxId && (
				<>
					<BoxTable box={box} />
					<br />
				</>
			)}

			<Form.Item
				label={'Intervention Buffer [h]'}
				rules={[{required: true}]}
				tooltip={
					'If an excursion is predicted, it will only trigger a deviation, if the time to the excursion is smaller than the intervention buffer. The predictions stop after qualified duration plus the buffer have passed.'
				}
			>
				<Slider
					disabled={!boxId}
					min={0}
					max={maxInterventionBufferHours}
					marks={marks}
					onChange={setInterventionBufferHours}
					value={interventionBufferHours}
				/>
			</Form.Item>

			<AlarmLimitSelect
				hideDelay={true}
				label={t(PredictiveTrans.alarmLimit)}
				onSelectCallback={setAlarmLimit}
				outUnit={props.outUnit}
				tooltip={t(PredictiveTrans.limitAlarmTooltip)}
				hideMultiZone={true}
			/>

			<Form.Item>
				<Space>
					<Button htmlType="submit" type="primary">
						Configure
					</Button>
					<Button onClick={reset} disabled={false}>
						Reset
					</Button>
				</Space>
			</Form.Item>
		</Form>
	);
}
